import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';

import { PrimaryLink } from '../components/Common/Links';
import MarketoForm from '../components/Common/MarketoForm/MarketoForm';
import PageSection from '../components/Common/PageSection';
import RichTextConverter from '../components/Common/RichText/RichTextConverter';
import SimpleCard from '../components/Common/SimpleCard';
import { LargeCard } from '../components/Generic/LargeCards';
import Layout from '../components/Layouts/Layout';
import MarketingSeo from '../components/MarketingSeo';
import { ReactComponent as Apps } from '../images/Apple-Android-Logos.svg';
import * as styles from './mobileApps.module.css';

const mobileApps = ({ data, location }) => {
  let page = {};
  if (data.page) page = data.page;

  const sections = [];
  let usedForm = false;
  let pageSections = page.pageSections || [];
  pageSections.forEach((section, i) => {
    switch (section.__typename) {
      case 'ContentfulGroupedContent': {
        sections.push(
          <PageSection key={i} className={section.backgroundColor === 'Light Blue' && styles.foam} curveTop>
            <div className={styles.threeUpHeading}>
              <h3>{section.title}</h3>
              <RichTextConverter copy={section.sectionCopy} />
            </div>
            {section.mainImage &&
              (section.mainImage?.gatsbyImageData ? (
                <GatsbyImage
                  alt={section.mainImage?.title || ''}
                  image={section.mainImage?.gatsbyImageData}
                  className={styles.threeUpImage}
                />
              ) : (
                <img
                  src={section.mainImage?.file?.url}
                  loading="lazy"
                  className={styles.threeUpImage}
                  alt={section.mainImage?.title || ''}
                />
              ))}
            <div className={styles.threeUp}>
              {section.content.map((card) => {
                const isSimpleCard = card.__typename === 'ContentfulSimpleCard';

                // If it's not a Simple Card, it's probably a blog post or resource
                return isSimpleCard ? (
                  <SimpleCard
                    key={`${card.title}-${i}`}
                    imageType={'icon'}
                    imageSrc={card.image?.file?.url}
                    title={card.title}
                    body={card.body}
                  />
                ) : (
                  <LargeCard
                    key={`${card.title}-${i}`}
                    imageFragment={card.heroImage}
                    url={card.__typename === 'ContentfulBlogPost' ? `/blog/${card.slug}` : `/resources/${card.slug}`}
                    ctaLabel={card.contentType.viewLabel}
                    title={card.title}
                    description={card.articleDescription.articleDescription}
                    type={card.contentType.title}
                    style={{ boxShadow: 'none', paddingBottom: '1.5rem' }}
                  />
                );
              })}
            </div>
          </PageSection>
        );
        break;
      }
      case 'ContentfulSimpleCard':
        const greyBackground = section.accentColor === 'Grey';
        sections.push(
          <PageSection key={i} className={greyBackground ? styles.alabaster : styles.curveBottom}>
            <div className={[styles.grid, i % 2 === 0 && styles.flipped].join(' ')}>
              {section.image && section.image?.gatsbyImageData ? (
                <GatsbyImage
                  alt={section.image?.title || ''}
                  image={section.image?.gatsbyImageData}
                  className={styles.image}
                />
              ) : (
                <img src={section.image?.file.url} loading="lazy" alt={section.image?.title || ''} />
              )}
              <div className={styles.copy}>
                <h3>{section.title}</h3>
                <RichTextConverter copy={section.body} />
              </div>
            </div>
          </PageSection>
        );
        break;

      case 'ContentfulMarketoForm':
        if (usedForm) {
          break;
        }
        usedForm = true;
        sections.push(
          <PageSection className={styles.grey}>
            <div className={styles.form}>
              <div className={styles.formCopy}>
                <RichTextConverter copy={section.formCopy} />
              </div>

              <div className={styles.formWrapper}>
                <MarketoForm formId={section.marketoFormId} />
              </div>
            </div>
          </PageSection>
        );
        break;

      default:
        break;
    }
  });

  return (
    <Layout>
      <MarketingSeo meta={page.pageMetaData} slug={location.pathname} />
      <PageSection isTopSection wide>
        <div className={styles.heroContainer}>
          <div className={styles.heroContent}>
            <h1>{page.heading}</h1>
            <RichTextConverter copy={page.headingIntroCopy} />
            <div>
              <PrimaryLink label={page.headingCtaLabel} url={page.headingCtaUrl} isButton backgroundColor="blue" />
              <div className={styles.apps}>
                <span>WORKS WITH iOS & ANDROID</span>
                <Apps />
              </div>
            </div>
          </div>
          <div className={styles.hero}>
            <img src={page.headingImage?.file.url} loading="lazy" alt={page.headingImage?.alt || ''} />
          </div>
        </div>
      </PageSection>
      {sections}
    </Layout>
  );
};

export default mobileApps;

export const query = graphql`
  query MobileAppsTemplateQuery($slug: String!) {
    page: contentfulMultipurposePageTemplate(slug: { eq: $slug }) {
      ...MultipurposePage
    }
  }
`;
